import React, { useEffect, useRef, useState, useCallback, useMemo, memo } from 'react';
import {
  ExternalLink,
  IconBannedUser,
  IconCollection,
  IconSpinner,
  ResponsiveImage,
  UserBadgeList,
  MediaLightbox,
} from '../../../../components';
import ProfileIconCard from '../../../../components/ProfileIconCard/ProfileIconCard';
import FeedInlineAttachments from '../FeedInlineAttachments';
import css from './CircleFeedCard.module.css';
import {
  abbreviateName,
  formatCreatedAt,
  getFirstImageItem,
  getRandomItemWithPriority,
} from '../../../../util/circleHelper';
import CommentContainer from '../CircleFeedModal/CommentContainer';
import AddCommentContainer from '../CircleFeedModal/AddCommentContainer';
import FeedDocumentAttachment from '../FeedDocumentAttachment/FeedDocumentAttachment';
import PollCard from '../Poll/PollCard';
import { useDispatch } from 'react-redux';
import { subscriptionPermissionRequest } from '../../../../ducks/user.duck';
import FeedCoverPhoto from '../FeedCoverPhoto/FeedCoverPhoto';

// import { FormattedMessage } from '../../../../util/reactIntl';
// import {
//   createSpacePost,
//   deleteCirclePost,
//   flaggedContent,
//   followUnfollowPost,
//   toggleCreatePost,
//   toggleEditPost,
//   toggleReportContent,
// } from '../../CircleCommunityPage.duck';
import PostOptionsMenu from './PostOptionsMenu';
import {
  checkElephantModalOpen,
  getCommunityUserRole,
  truncateTextByCharacters,
} from '../../../../util/data';
import {
  fetchPostCommentList,
  getMemberDetail,
  toggleCreateComment,
  toggleReplyComment,
} from '../../CircleCommunityPage.duck';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import { useHistory } from 'react-router-dom';
import { createResourceLocatorString } from '../../../../util/routes';
import { toastifyFn } from '../../../../components/ReactToastify/ReactToastify';
import { FormattedMessage } from '../../../../util/reactIntl';
import LinkPreviewComponent from '../../../../components/LinkPreviewComponent/LinkPreviewComponent';
import InReviewCard from '../../../../components/InReviewCard/InReviewCard';

const checkContentLength = content => {
  let length = 0;

  Array.isArray(content) &&
    content.forEach(item => {
      if (item.text) {
        length += item.text.length;
      }
    });

  return length;
};

const stripHtmlToText = html => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const buildFallbackTiptapContent = feedData => {
  const text =
    feedData?.body_plain_text ||
    feedData?.body_plain_text_without_attachments ||
    (typeof feedData?.body === 'string' ? feedData.body : '') ||
    stripHtmlToText(feedData?.body?.html);

  if (!text) {
    return [];
  }

  return [
    {
      type: 'paragraph',
      content: [
        {
          type: 'text',
          text,
          circle_ios_fallback_text: text,
        },
      ],
    },
  ];
};

const calculateTotalTextLength = (content, textLength = 300) => {
  let totalLength = 0;
  let indexOf300 = -1;

  Array.isArray(content) &&
    content.forEach((item, index) => {
      if (item.type === 'paragraph') {
        totalLength += checkContentLength(item.content);
      }

      if (['bulletList', 'orderedList'].includes(item.type) && Array.isArray(item.content)) {
        item.content.forEach(listItem => {
          if (listItem.type === 'listItem' && listItem.content) {
            listItem.content.forEach(paragraph => {
              if (paragraph.type === 'paragraph' && paragraph.content) {
                totalLength += checkContentLength(paragraph.content);
              }
            });
          }
        });
      }

      if (totalLength > textLength && indexOf300 === -1) {
        indexOf300 = index; // Capture the first index where totalLength exceeds 300
      }
    });

  return {
    contentTotalLength: totalLength,
    contentIndex: indexOf300,
  };
};

const handleTagUser = async (sgids_to_object_map, sgid, dispatch) => {
  const matchItem = sgids_to_object_map[sgid];
  if (!matchItem) {
    toastifyFn('error', 'User not found');
    return;
  }

  const newTab = window.open('', '_blank'); // Open a blank tab first (iOS requires this)

  const { id } = matchItem;
  const { headline } = await dispatch(getMemberDetail(id));

  if (headline) {
    newTab.location.href = `/u/${headline}`; // Redirect to the actual URL
  } else {
    newTab.close();
    toastifyFn('error', 'User profile not found');
  }
};

// Helper function to render mentions (e.g., @Atal Kaushik)
const renderMention = (mention, sgids_to_object_map = {}, dispatch, postId, index = 0) => {
  return (
    <span
      className={css.mention}
      key={postId + index + mention.attrs.sgid + 'mention'}
      onClick={() => handleTagUser(sgids_to_object_map, mention.attrs.sgid, dispatch)}
    >
      {mention.circle_ios_fallback_text}
    </span>
  );
};

const renderEmbed = (item, sgids_to_object_map, postId = 'postId', index = 0) => {
  if (item?.type !== 'embed') return null;
  const sgid = item.attrs?.sgid;
  const embedData = sgids_to_object_map[sgid];
  if (!embedData) return null;
  return embedData?.embed_type === 'link' ? (
    <LinkPreviewComponent
      url={embedData?.url}
      keyID={postId + index + 'linkPreview' + embedData?.id}
    />
  ) : null;
};

export const RenderContent = ({
  content,
  onHandleItemClick,
  isShowMoreButtonVisible,
  sgids_to_object_map = {},
  dispatch,
  postId,
  showEditedLabel = false,
}) => {
  const lastParagraphIndex = Array.isArray(content)
    ? content.map(item => item?.type).lastIndexOf('paragraph')
    : -1;

  return content.map((item, index) => {
    const shouldRenderEditedLabel = showEditedLabel && index === lastParagraphIndex;

    switch (item.type) {
      case 'horizontalRule':
        return <hr className={css.spacerLine} key={postId + index + 'horizontalRule'} />;

      case 'heading': {
        const HeadingTag = `h${item.attrs.level}`;
        return (
          <HeadingTag className={css.headingWrapper} key={postId + index + 'heading'}>
            {Array.isArray(item?.content) && item?.content.length && item?.content[0]?.text}
          </HeadingTag>
        );
      }

      case 'blockquote':
        return (
          <blockquote className={css.contentItemText} key={postId + index + 'blockquote'}>
            {item.content[0]?.content.map((contentItem, subIndex) =>
              contentItem.type === 'text' ? (
                contentItem.text
              ) : contentItem.type === 'hardBreak' ? (
                <div key={postId + index + 'contentItem-' + subIndex + 'hardBreak'} />
              ) : null
            )}
          </blockquote>
        );

      case 'codeBlock':
        return (
          <pre className={css.inputField} key={postId + index + 'codeBlock'}>
            <code>{item.content[0]?.text || ''}</code>
          </pre>
        );

      case 'cta':
        return (
          <a href={item.attrs.url} className={css.testButton} key={postId + index + 'cta'}>
            {item.attrs.label}
          </a>
        );

      case 'embed':
        return renderEmbed(item, sgids_to_object_map, postId, index + 'embed' + postId);

      case 'paragraph':
        const showMoreButton =
          index + 1 == content.length &&
          isShowMoreButtonVisible &&
          checkContentLength(item.content) > 300 ? (
            <div
              className={css.seeMoreButton}
              onClick={onHandleItemClick}
              key={postId + index + 'seeMoreButton'}
            >
              <FormattedMessage id="CircleFeedCard.seeMore" />
            </div>
          ) : null;

        if (!item?.content?.length) {
          return <></>;
        }

        return (
          <div key={postId + index + '--paragraph'}>
            <div className={css.contentWrapper}>
              <p key={postId + index + '--paragraph-p'}>
                {item.content &&
                  item.content.map((subItem, subIndex) => {
                    const fallbackText =
                      subItem?.text ||
                      subItem?.circle_ios_fallback_text ||
                      subItem?.attrs?.label ||
                      '';
                    const subItemText =
                      isShowMoreButtonVisible && index + 1 == content.length
                        ? truncateTextByCharacters(fallbackText, 300)
                        : fallbackText;

                    const uniqueKey = `${postId}-paragraph-${index}-${subIndex}-uniqueKey-`;

                    const isBold =
                      subItem.marks && subItem.marks.some(mark => mark.type === 'bold');
                    const isLink =
                      subItem.marks && subItem.marks.some(mark => mark.type === 'link');
                    const isMention = subItem.type === 'mention';

                    if (subItem.type === 'hardBreak') {
                      return <br key={uniqueKey + '--hardBreak'} />;
                    }

                    if (isMention) {
                      return renderMention(
                        subItem,
                        sgids_to_object_map,
                        dispatch,
                        postId,
                        uniqueKey + '--mention'
                      );
                    }

                    if (isBold) {
                      return <b key={uniqueKey + '--bold'}>{subItemText}</b>;
                    }

                    if (isLink) {
                      return (
                        <LinkPreviewComponent
                          url={subItemText}
                          key={uniqueKey + '--linkPreview'}
                          keyID={uniqueKey + '--linkPreview'}
                        />
                      );
                    }

                    // Render text with optional bold styling
                    return (
                      <React.Fragment key={uniqueKey + '-' + 'text'}>
                        {subItemText?.split('\n').map((e, i) => (
                          <React.Fragment key={uniqueKey + i + '--line'}>
                            {e}
                            {i < subItemText.split('\n').length - 1 && <br />}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    );
                  })}
                {shouldRenderEditedLabel ? (
                  <span className={css.inlineEditedText}> (edited)</span>
                ) : null}
              </p>
            </div>
            {showMoreButton}
          </div>
        );

      case 'bulletList':
        return (
          <ul className={css.listItemWrapper} key={postId + index + 'bulletList'}>
            {Array.isArray(item.content) &&
              item.content.map((listItem, listIndex) => (
                <li key={postId + index + listIndex + 'bulletList'}>
                  <div className={css.paragraphText}>
                    {listItem.content.map((listContent, listContentIndex) => {
                      if (listContent?.type === 'embed') {
                        return renderEmbed(
                          listContent,
                          sgids_to_object_map,
                          postId,
                          index + listIndex + listContentIndex + '-bulletList-'
                        );
                      } else {
                        return listContent.content
                          ?.filter(item => item?.type !== 'embed')
                          .map((textItem, textIndex) => {
                            if (textItem.type === 'mention') {
                              return React.cloneElement(
                                renderMention(textItem, sgids_to_object_map, dispatch),
                                {
                                  key:
                                    postId +
                                    index +
                                    listIndex +
                                    '-' +
                                    listContentIndex +
                                    '-' +
                                    textIndex +
                                    '-mention',
                                }
                              );
                            } else if (textItem.type === 'text') {
                              const linkMark = textItem.marks?.find(mark => mark.type === 'link');
                              if (linkMark) {
                                return (
                                  <LinkPreviewComponent
                                    url={linkMark.attrs.href}
                                    keyID={
                                      postId +
                                      '-bulletList' +
                                      index +
                                      listIndex +
                                      '-' +
                                      listContentIndex +
                                      '-' +
                                      textIndex +
                                      '-linkPreview'
                                    }
                                  />
                                );
                              }
                              return (
                                <span
                                  key={
                                    postId +
                                    index +
                                    listIndex +
                                    '-' +
                                    listContentIndex +
                                    '-' +
                                    textIndex +
                                    '-text'
                                  }
                                >
                                  {textItem.text}
                                </span>
                              );
                            }
                            return null;
                          });
                      }
                    })}
                  </div>
                </li>
              ))}
          </ul>
        );

      case 'orderedList':
        return (
          <ol className={css.orderList} key={postId + 'orderedList' + 'index' + index}>
            {Array.isArray(item.content)
              ? item.content.map((listItem, listIndex) => (
                  <li key={postId + 'orderedList' + 'listIndex' + listIndex}>
                    {listItem.content.map((listContent, listContentIndex) =>
                      listContent.content.map((textItem, textIndex) => (
                        <span
                          key={
                            postId +
                            index +
                            listIndex +
                            '-' +
                            listContentIndex +
                            '-' +
                            textIndex +
                            '-orderedList'
                          }
                        >
                          {textItem.text}
                        </span>
                      ))
                    )}
                  </li>
                ))
              : null}
          </ol>
        );

      default:
        return <React.Fragment key={postId + 'default' + index}></React.Fragment>;
    }
  });
};

const CircleFeedCard = props => {
  const {
    feedData,
    onPostLikeDislike,
    isredirectToLogin = false,
    onCreateBookMark,
    onDeleteBookMark,
    setIsFeedModal,
    handleItemClick = () => {},
    isSavedPost = false,
    postCommentListData = {},
    currentCommunityMember = {},
    requestCreateComment,
    checkCreatePostAuth,
    requestDirectUpload = false,
    isRequestSpace,
    requestDeletePost,
    advancedSearchData,
    onAdvancedSearch,
    isReplyComment = false,
    loadMoreComments = false,
    isAddComment = false,
    editCommentData = null,
    setIsCommentpressed = () => {},
    spaceDetail = {},
    userModeratorsGroups,
    onSetElephantVerificationModal = () => {},
    currentUser,
    handleCloseFeedModal = () => {},
    userBadges = [],
    hasActiveSubscription = false,
    currentUserHasActiveSubscription = false,
    hasStripeConnected = false,
    currentUserHasStripeConnected = false,
    isBasicRole = false,
    pollActionInProgressById = {},
    pollActionErrorById = {},
    onSubmitPollVote,
    onClosePoll,
    onPostDeleted = () => {},
  } = props;
  const { page = 1, per_page = 10, has_next_page = false, count = 0, records = [] } =
    postCommentListData || {};
  const { avatar_url: current_avatar_url, name: currentUserName } = currentCommunityMember || {};
  const currentVoterProfile = {
    name: currentUserName || '',
    avatarUrl: current_avatar_url || '',
  };

  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [lightboxMedia, setLightboxMedia] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // memoize feedData by id so small parent re-renders that recreate the object don't force this component to re-render
  const feedDataMemo = feedData || {};

  const {
    id: postId,
    display_title,
    name,
    is_comments_closed,
    is_comments_enabled,
    is_liking_enabled,
    comment_count,
    user_likes_count,
    is_liked,
    tiptap_body,
    space,
    author,
    bookmark_id,
    created_at,
    updated_at,
    cover_image = '',
    slug,
    flagged_for_approval_at = null,
    userRoleInGroup: authorRoleInGroup,
    user_avatar_url,
    user_name,
    space_name,
    likes_count,
    comments_count,
    isEdited,
    poll,
    pollHydrationError,
  } = feedDataMemo || {};
  const pollActionName = poll?.id ? pollActionInProgressById[poll.id] : null;

  const showEditedLabel =
    isEdited ||
    (created_at &&
      updated_at &&
      new Date(updated_at).getTime() > new Date(created_at).getTime() + 1000);
  const { content: tiptapContent = [] } = tiptap_body?.body || {};
  const content = tiptapContent.length ? tiptapContent : buildFallbackTiptapContent(feedDataMemo);
  const isRenderableAttachment = attachment =>
    attachment && typeof attachment === 'object' && typeof attachment.content_type === 'string';
  const tiptapAttachments = Array.isArray(tiptap_body?.attachments)
    ? tiptap_body.attachments.filter(isRenderableAttachment)
    : [];
  const tiptapInlineAttachments = Array.isArray(tiptap_body?.inline_attachments)
    ? tiptap_body.inline_attachments.filter(isRenderableAttachment)
    : [];
  const imageAttachments = tiptapAttachments.filter(e => e.content_type?.startsWith('image/'));
  const nonImageAttachments = tiptapAttachments.filter(e => !e.content_type?.startsWith('image/'));
  const sgidsToObjectMap = tiptap_body?.sgids_to_object_map || {};
  const { id: spaceId, name: spaceName, slug: spaceSlug } = space || {};

  const { name: authorName, avatar_url, roles = [], headline, community_member_id } = author || {};

  const authorRole = roles.includes('admin')
    ? 'admin'
    : roles && roles.includes('moderator')
    ? 'moderator'
    : null;

  const userRoleInGroup = getCommunityUserRole(userModeratorsGroups, spaceId);
  const hasMoreItem = tiptapInlineAttachments.length + tiptapAttachments.length;

  const firstItem = tiptapInlineAttachments.length
    ? tiptapInlineAttachments[0]
    : tiptapAttachments[0];

  const hideSpacer = is_comments_enabled || is_liking_enabled;

  const handlePostLikeDislike = useCallback(async () => {
    if (isredirectToLogin) {
      onPostLikeDislike(is_liked, postId, spaceId);
      return;
    }
    if (!checkCreatePostAuth) {
      dispatch(subscriptionPermissionRequest(true));
      return;
    }
    const isNotPermission = checkElephantModalOpen(currentUser);
    if (isNotPermission) {
      onSetElephantVerificationModal(true);
      return;
    }
    onPostLikeDislike(is_liked, postId, spaceId);
  }, [checkCreatePostAuth, dispatch, onPostLikeDislike, is_liked, postId, spaceId]);

  const handleSaveUnsave = useCallback(() => {
    if (!checkCreatePostAuth) {
      dispatch(subscriptionPermissionRequest(true));
      return;
    }

    if (bookmark_id) {
      onDeleteBookMark({ bookmarkId: bookmark_id, postId, spaceId });
    } else {
      const apiParams = {
        record_id: postId,
        bookmark_type: 'post',
        postId,
        spaceId,
      };
      onCreateBookMark(apiParams);
    }
    setIsOpen(v => !v);
  }, [
    checkCreatePostAuth,
    dispatch,
    bookmark_id,
    onDeleteBookMark,
    onCreateBookMark,
    postId,
    spaceId,
  ]);

  const onHandleItemClick = useCallback(
    e => {
      // Prevent opening modal if clicking on interactive elements
      if (e?.target) {
        const target = e.target;
        const isInteractiveElement =
          target.closest('button') ||
          target.closest('a') ||
          target.closest('input') ||
          target.closest('textarea') ||
          target.closest('[role="button"]') ||
          target.closest('[contenteditable="true"]');

        if (isInteractiveElement) {
          return;
        }
      }

      if (isSavedPost) return;
      handleItemClick();
    },
    [isSavedPost, handleItemClick]
  );

  const textLength = 300;
  const { contentTotalLength, contentIndex } =
    content && content.length ? calculateTotalTextLength(content, textLength) : {};

  const hasPollAttachment = attachmentList => {
    return attachmentList?.some(attachment => attachment?.type === 'poll');
  };

  if (hasPollAttachment(tiptapInlineAttachments)) {
    return null; // Return null to prevent rendering
  }

  const handleSpaceClick = useCallback(() => {
    if (currentUser?.id?.uuid) {
      history.push(
        createResourceLocatorString(
          'JoinedGroupsCommunityPage',
          routeConfiguration,
          { id: spaceId, slug: spaceSlug },
          {}
        )
      );
    } else {
      history.push('/login');
    }
  }, [history, routeConfiguration, spaceId, spaceSlug]);

  const handleCommentSection = useCallback(() => {
    dispatch(toggleReplyComment());
    dispatch(toggleCreateComment(true));
  }, [dispatch]);

  const handleLoadMore = useCallback(() => {
    const params = { page: page + 1, per_page, postId };
    dispatch(fetchPostCommentList(params));
  }, [page, per_page, postId, dispatch]);
  const isFirstLoad = useRef(true); // Ref to track the first load
  //is_comments_closed is true then close section to add comment but list cooment
  //is_comments_enabled is false then close whole seection

  const isOwnPost = headline && currentUser?.id?.uuid && headline == currentUser?.id?.uuid;

  const handleAuthorClick = useCallback(() => {
    if (currentUser?.id?.uuid) {
      history.push(`/u/${headline}`);
    } else {
      history.push('/login');
    }
  }, [history, currentUser, headline]);

  // Filter media for lightbox (exclude GIFs)
  const getLightboxMedia = useCallback(() => {
    // Combine in the same order as they appear in the UI
    const allMedia = [...tiptapInlineAttachments, ...tiptapAttachments];

    return allMedia.filter(item => {
      const isGif = item.content_type === 'image/gif' || item.filename === 'giphy.gif';
      const isImage = item.content_type?.startsWith('image/');
      const urlOrName = item.url || item.filename || '';
      const isVideo =
        item.content_type?.startsWith('video/') ||
        /\.(mp4|mov|webm|m4v)$/i.test(urlOrName) ||
        !!item.video_variants?.hls;

      return !isGif && (isImage || isVideo);
    });
  }, [tiptapInlineAttachments, tiptapAttachments]);

  const handleMediaClick = useCallback(
    (clickedMedia, clickedIndex) => {
      const media = getLightboxMedia();

      // Try to find by id first, then by url, then by a combination of properties
      let actualIndex = -1;

      if (clickedMedia.id) {
        actualIndex = media.findIndex(m => m.id === clickedMedia.id);
      }

      if (actualIndex === -1 && clickedMedia.url) {
        actualIndex = media.findIndex(m => m.url === clickedMedia.url);
      }

      // If still not found, try matching by filename
      if (actualIndex === -1 && clickedMedia.filename) {
        actualIndex = media.findIndex(m => m.filename === clickedMedia.filename);
      }

      // If still not found, try matching by content_type and size
      if (actualIndex === -1) {
        actualIndex = media.findIndex(
          m =>
            m.content_type === clickedMedia.content_type && m.byte_size === clickedMedia.byte_size
        );
      }

      setLightboxMedia(media);
      setLightboxIndex(actualIndex >= 0 ? actualIndex : 0);
    },
    [getLightboxMedia]
  );

  const handleCloseLightbox = useCallback(() => {
    setLightboxMedia([]);
    setLightboxIndex(0);
  }, []);

  const handleNextMedia = useCallback(() => {
    setLightboxIndex(prev => Math.min(prev + 1, lightboxMedia.length - 1));
  }, [lightboxMedia.length]);

  const handlePrevMedia = useCallback(() => {
    setLightboxIndex(prev => Math.max(prev - 1, 0));
  }, []);
 const isPollPost = poll?.id;
  return (
    <div className={css.feedcardWrapper} key={postId + 'feedcardWrapper'}>
      <div className={css.topImage} onClick={onHandleItemClick}>
        {/* new */}
        <FeedCoverPhoto keyID={postId + 'feedCoverPhoto'} coverPhoto={cover_image} />
      </div>
      <div className={css.userDetails}>
        {flagged_for_approval_at ? (
          <span className={css.reviewText}>
            <InReviewCard keyID={postId + 'inReviewCard'} />
          </span>
        ) : null}
        <div className={css.bodyWrapper}>
          <div className={css.userWrapper}>
            <div className={css.imageUserDetails}>
              {!headline && authorName == 'Inactive User' ? (
                <IconBannedUser className={css.nameWrapper} />
              ) : (
                <div onClick={handleAuthorClick} className={css.imageWrapper}>
                  {avatar_url || user_avatar_url ? (
                    <ResponsiveImage
                      alt={'FeedCoverPhoto'}
                      imagewithURL={avatar_url || user_avatar_url}
                    />
                  ) : (
                    <div className={css.nameWrapper}>{abbreviateName(authorName)}</div>
                  )}
                  {hasStripeConnected ? (
                    <div className={css.verfiedIcon}>
                      <IconCollection name="profile_badge_stripe" />
                    </div>
                  ) : hasActiveSubscription ? (
                    <div className={css.verfiedIcon}>
                      <IconCollection name="profile_badge_elephant" />
                    </div>
                  ) : null}
                </div>
              )}
              <div className={css.authorDetailsContainer}>
                <div className={css.authorZone}>
                  <span className={css.authorName}>{authorName || user_name}</span>
                  {authorRoleInGroup ? (
                    <span className={css.moderatorText}>{authorRoleInGroup}</span>
                  ) : null}

                  <span className={css.timingZone}>{formatCreatedAt(created_at)}</span>
                  <div className={css.postedText}>
                    <div className={css.postedSpaceText}>
                      Posted in <b onClick={handleSpaceClick}>{spaceName || space_name}</b>
                    </div>
                    <span className={css.timingZoneMobile}>{formatCreatedAt(created_at)}</span>
                  </div>
                </div>
                {userBadges && userBadges.length > 0 && (
                  <UserBadgeList badges={userBadges} maxDisplay={1} size="small" />
                )}
              </div>
            </div>
            <div className={css.rightFuntionButton}>
              <div className={css.shareWrapper}>
                {/* <div onClick={handleSaveUnsave}>
                  {bookmark_id ? <ProfileIconCard type="unsave" /> : <ProfileIconCard type="save" />}
                </div> */}
                {/* <span className={css.shareText}>Share</span> */}
              </div>
              {/* options menu */}
              {currentUser ? (
                <PostOptionsMenu
                  feedData={feedData}
                  currentCommunityMember={currentCommunityMember}
                  handleSaveUnsave={handleSaveUnsave}
                  userRoleInGroup={userRoleInGroup}
                  isOwnPost={isOwnPost}
                  currentUser={currentUser}
                  requestDeletePost={requestDeletePost}
                  onPostDeleted={onPostDeleted}
                  onSetElephantVerificationModal={onSetElephantVerificationModal}
                />
              ) : null}
            </div>
          </div>
          <div
            data-testid="circle-feed-card-title"
            className={css.titleText}
            onClick={onHandleItemClick}
          >
            {name}
          </div>
          <RenderContent
            content={content}
            onHandleItemClick={onHandleItemClick}
            isShowMoreButtonVisible={!isSavedPost}
            sgids_to_object_map={sgidsToObjectMap}
            dispatch={dispatch}
            postId={postId}
            showEditedLabel={showEditedLabel}
          />

          {imageAttachments.length > 0 ? (
            <FeedInlineAttachments
              // firstImage={firstImage}
              onHandleItemClick={onHandleItemClick}
              onMediaClick={handleMediaClick}
              inline_attachments={isSavedPost ? tiptapInlineAttachments : null}
              attachments={
                isSavedPost ? imageAttachments : imageAttachments.filter((st, i) => i < 1)
              }
              isRequestSpace={isRequestSpace}
            />
          ) : null}
          {nonImageAttachments.length > 0 ? (
            <div className={css.tiptapContent}>
              <FeedInlineAttachments
                // firstImage={firstImage}
                onHandleItemClick={onHandleItemClick}
                onMediaClick={handleMediaClick}
                inline_attachments={isSavedPost ? tiptapInlineAttachments : null}
                attachments={
                  isSavedPost ? nonImageAttachments : nonImageAttachments.filter((st, i) => i < 1)
                }
                isRequestSpace={isRequestSpace}
              />
            </div>
          ) : null}
          {/* show documents */}
          <FeedDocumentAttachment
            inline_attachments={tiptapInlineAttachments}
            attachments={tiptapAttachments}
          />
          <PollCard
            poll={poll}
            hydrationError={pollHydrationError}
            voteInProgress={pollActionName === 'vote'}
            closeInProgress={pollActionName === 'close'}
            errorMessage={poll?.id ? pollActionErrorById[poll.id] : null}
            onVote={onSubmitPollVote}
            onClose={onClosePoll}
            currentVoterProfile={currentVoterProfile}
          />
          {hasMoreItem > 1 && !isSavedPost ? (
            <div className={css.seeMoreButton} onClick={onHandleItemClick}>
              <FormattedMessage id="CircleFeedCard.seeMore" />
            </div>
          ) : null}
        </div>
        {/* add images here instead of text */}
      </div>
      {hideSpacer ? <div className={css.spacer}></div> : null}
      <div className={css.functionWrapper}>
        <div className={css.likeSection}>
          {is_liking_enabled ? (
            <div onClick={handlePostLikeDislike}>
              {is_liked ? <ProfileIconCard type="liked" /> : <ProfileIconCard type="like" />}
            </div>
          ) : null}
          {is_comments_enabled && !is_comments_closed ? (
            <div
              onClick={e => {
                e.stopPropagation();
                onHandleItemClick(e);
                setIsCommentpressed(true);
                handleCommentSection();
              }}
              data-testid="feed-card-comment"
            >
              <ProfileIconCard type="comment" />
            </div>
          ) : null}
        </div>
        <div className={css.rightLikeSection}>
          {is_comments_enabled ? (
            <span
              onClick={e => {
                e.stopPropagation();
                onHandleItemClick(e);
                setIsCommentpressed(true);
              }}
            >
              {comment_count || comments_count}{' '}
              {comment_count === 1 || comments_count === 1 ? 'comment' : 'comments'}
              {/* <FormattedMessage id='CircleCommunity.comments' values={{ count: comment_count }}/>  */}
            </span>
          ) : null}
          {is_liking_enabled ? (
            <span>
              {user_likes_count || likes_count}{' '}
              {user_likes_count === 1 || likes_count === 1 ? 'like' : 'likes'}
            </span>
          ) : null}
        </div>
      </div>
      {isSavedPost && is_comments_closed && !!is_comments_enabled ? (
        <div className={css.commentLockedText}>
          <ProfileIconCard type="lock" />
          <span>comments are locked</span>
        </div>
      ) : null}
      {/* comment section here */}
      {isSavedPost && is_comments_enabled ? (
        <div className={css.commentSection}>
          {/* see comments */}
          {postCommentListData?.records
            ?.filter(comments => comments?.post_id == feedData?.id)
            .map(record => (
              <CommentContainer
                handleCloseFeedModal={handleCloseFeedModal}
                onSetElephantVerificationModal={onSetElephantVerificationModal}
                key={record.id}
                commentData={record}
                postId={postId}
                spaceId={spaceId}
                spaceName={spaceName}
                spaceSlug={spaceSlug}
                currentCommunityMember={currentCommunityMember}
                isRequestSpace={isRequestSpace}
                requestCreateComment={requestCreateComment}
                requestDirectUpload={requestDirectUpload}
                advancedSearchData={advancedSearchData}
                onAdvancedSearch={onAdvancedSearch}
                isFirstLoad={isFirstLoad}
              />
            ))}

          {loadMoreComments ? (
            <div className={css.spinnerWrapper}>
              <IconSpinner type="true" />
            </div>
          ) : has_next_page ? (
            <div className={css.showMoreButton} onClick={handleLoadMore}>
              <FormattedMessage id="CircleCommunity.loadMoreComments" />
            </div>
          ) : null}
          {isReplyComment || (!isAddComment && !editCommentData) ? (
            <div className={css.addCommentSection}>
              <div className={css.imageContainer}>
                {current_avatar_url ? (
                  <ResponsiveImage imagewithURL={current_avatar_url} />
                ) : (
                  <div className={css.nameWrapper}>{abbreviateName(currentUserName)}</div>
                )}
                {currentUserHasStripeConnected ? (
                  <div className={css.verfiedIcon}>
                    <IconCollection name="profile_badge_stripe" />
                  </div>
                ) : currentUserHasActiveSubscription ? (
                  <div className={css.verfiedIcon}>
                    <IconCollection name="profile_badge_elephant" />
                  </div>
                ) : null}
              </div>
              <div
                className={css.addCommentContainer}
                onClick={e => {
                  e.stopPropagation();
                  handleCommentSection();
                }}
              >
                <FormattedMessage id="CircleCommunity.commentPlaceholder" />
              </div>
            </div>
          ) : null}
          {!is_comments_closed && (editCommentData || (!isReplyComment && isAddComment)) ? (
            <AddCommentContainer
              currentCommunityMember={currentCommunityMember}
              postId={postId}
              spaceId={spaceId}
              spaceName={spaceName}
              spaceSlug={spaceSlug}
              requestCreateComment={requestCreateComment}
              requestDirectUpload={requestDirectUpload}
              onAdvancedSearch={onAdvancedSearch}
              advancedSearchData={advancedSearchData}
              currentUser={currentUser}
              onSetElephantVerificationModal={onSetElephantVerificationModal}
              isBasicRole={isBasicRole}
              editCommentData={editCommentData}
            />
          ) : null}
        </div>
      ) : null}

      {/* Media Lightbox */}
      {lightboxMedia.length > 0 && (
        <MediaLightbox
          media={lightboxMedia}
          currentIndex={lightboxIndex}
          onClose={handleCloseLightbox}
          onNext={handleNextMedia}
          onPrev={handlePrevMedia}
        />
      )}
    </div>
  );
};

export default memo(CircleFeedCard);
